var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { noop, throttle } from '@ally/utilitarian';
import { LoadState } from './types';
function getDomain(_a) {
    var data = _a.data, error = _a.error, isUpdating = _a.isUpdating, response = _a.response, loadState = _a.loadState;
    return {
        data: data !== null && data !== void 0 ? data : null,
        error: error !== null && error !== void 0 ? error : null,
        update: noop,
        response: response !== null && response !== void 0 ? response : null,
        status: {
            isUpdating: isUpdating !== null && isUpdating !== void 0 ? isUpdating : false,
            loadState: loadState,
            timeStamp: new Date(),
        },
    };
}
export function getDefaultDomainState(data) {
    return getDomain({ loadState: LoadState.UNLOADED, data: data });
}
/**
 * A hook for the fetching and maintaining (API) data.
 * Given a data shape (T), fetch function and some initial data, will return
 * a memoized "data wrapper" in the shape:
 *
 * const state = {
 *   data: T | null
 *   error: Error | null,
 *   update: () => void,
 *   status: {
 *     loadState: LoadState,
 *     timeStamp: Date
 *   }
 * }
 *
 * Where `update` will re-fetch and update the data state.
 */
export function useDataDomain(_a) {
    var fetch = _a.fetch, _b = _a.fireLoad, fireLoad = _b === void 0 ? false : _b, _c = _a.fireUnload, fireUnload = _c === void 0 ? false : _c, _d = _a.defaultData, defaultData = _d === void 0 ? null : _d, _e = _a.throttleDelayMS, throttleDelayMS = _e === void 0 ? 500 : _e;
    var _f = __read(useState(getDefaultDomainState(defaultData)), 2), state = _f[0], setState = _f[1];
    var _g = __read(useState(false), 2), hasFired = _g[0], setHasFired = _g[1];
    var _h = __read(useState(false), 2), hasLoaded = _h[0], setHasLoaded = _h[1];
    var mountRef = useRef(true);
    useEffect(function () { return function () {
        mountRef.current = false;
    }; }, []);
    // A throttled function that's invocation is choked to `throttleDelayMS` ms.
    // Fetches the data and updates the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var update = useCallback(throttle(throttleDelayMS, function call() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(this, void 0, void 0, function () {
            var LOADED, LOADING, FAILED, setData, setLoaded;
            return __generator(this, function (_a) {
                LOADED = LoadState.LOADED, LOADING = LoadState.LOADING, FAILED = LoadState.FAILED;
                setData = mountRef.current ? setState : noop;
                setLoaded = mountRef.current ? setHasLoaded : noop;
                setData(function (prev) {
                    return getDomain({
                        data: prev.data,
                        isUpdating: hasLoaded,
                        loadState: LOADING,
                    });
                });
                Promise.resolve()
                    .then(function () { return fetch.apply(void 0, __spread(args)); })
                    .then(function (data) {
                    setLoaded(true);
                    setData(getDomain({
                        data: data === null || data === void 0 ? void 0 : data.payload,
                        response: data === null || data === void 0 ? void 0 : data.response,
                        loadState: LOADED,
                    }));
                })
                    .catch(function (error) {
                    setData(function (prev) {
                        return getDomain({
                            data: prev.data,
                            error: error,
                            loadState: FAILED,
                        });
                    });
                });
                return [2 /*return*/];
            });
        });
    }), [fetch, throttleDelayMS, hasLoaded]);
    var handleFireLoad = function () {
        if (!hasFired && fireLoad) {
            update();
            setHasFired(true);
        }
    };
    var handleFireUnload = function () {
        if (fireUnload) {
            setHasLoaded(false);
            setState(getDefaultDomainState(defaultData));
        }
    };
    useEffect(handleFireLoad, [update, fireLoad, hasFired]);
    useEffect(handleFireUnload, [fireUnload, defaultData]);
    return useMemo(function () { return (__assign(__assign({}, state), { update: update })); }, [state, update]);
}
//# sourceMappingURL=use-data-domain.js.map